// Xcelio brand mark — the "Orb X": a smouldering blue-grey sphere (the exact orb
// used across the app) with a bold warm-gold "X" at its centre. Clean scalable SVG,
// sharp at every size (navbar → favicon). Casing is locked as "Xcelio".

export function LogoMark({ size = 40, mono = null, className = "", active = false }) {
  // mono: null = full colour orb; "white"/"black" = flat single-colour mark
  const uid = "orb";
  if (mono) {
    const c = mono === "white" ? "#FFFFFF" : "#000000";
    return (
      <svg width={size} height={size} viewBox="0 0 120 120" fill="none"
        className={className} xmlns="http://www.w3.org/2000/svg" aria-label="Xcelio mark" role="img">
        <circle cx="60" cy="60" r="55" stroke={c} strokeWidth="4" fill="none" />
        <g stroke={c} strokeWidth="13" strokeLinecap="round">
          <line x1="42" y1="42" x2="78" y2="78" />
          <line x1="78" y1="42" x2="42" y2="78" />
        </g>
      </svg>
    );
  }
  return (
    <svg width={size} height={size} viewBox="0 0 120 120" fill="none"
      className={className} xmlns="http://www.w3.org/2000/svg" aria-label="Xcelio mark" role="img">
      <defs>
        {/* exact app orb: radial from silvery blue-grey highlight → deep navy */}
        <radialGradient id={`${uid}Grad`} cx="42%" cy="38%" r="72%">
          <stop offset="0%" stopColor="#d3dcea" />
          <stop offset="24%" stopColor="#93a6be" />
          <stop offset="50%" stopColor="#566579" />
          <stop offset="78%" stopColor="#2d3543" />
          <stop offset="100%" stopColor="#141922" />
        </radialGradient>
        <linearGradient id={`${uid}Gold`} x1="40" y1="40" x2="80" y2="80" gradientUnits="userSpaceOnUse">
          <stop stopColor="#f7e6bd" />
          <stop offset="0.5" stopColor="#e6c88a" />
          <stop offset="1" stopColor="#c99a4f" />
        </linearGradient>
        <radialGradient id={`${uid}Halo`} cx="50%" cy="50%" r="50%">
          <stop offset="60%" stopColor="#c8d6ea" stopOpacity="0" />
          <stop offset="100%" stopColor="#c8d6ea" stopOpacity={active ? 0.55 : 0.28} />
        </radialGradient>
        <filter id={`${uid}Soft`} x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="2.2" />
        </filter>
      </defs>

      {/* faint outer halo */}
      <circle cx="60" cy="60" r="59" fill={`url(#${uid}Halo)`} />
      {/* the orb */}
      <circle cx="60" cy="60" r="55" fill={`url(#${uid}Grad)`} />
      {/* smouldering smoke wisps inside */}
      <g style={{ mixBlendMode: "screen" }} filter={`url(#${uid}Soft)`}>
        <ellipse cx="46" cy="48" rx="20" ry="14" fill="#c4d2e6" opacity="0.16" />
        <ellipse cx="74" cy="72" rx="18" ry="13" fill="#1c2330" opacity="0.30" />
      </g>
      {/* top-left specular highlight */}
      <ellipse cx="43" cy="37" rx="13" ry="9" fill="#eef3fb" opacity="0.35" filter={`url(#${uid}Soft)`} />

      {/* bold gold X */}
      <g strokeLinecap="round">
        <g stroke="#8a6a1f" strokeWidth="14" opacity="0.35">
          <line x1="42" y1="42" x2="78" y2="78" />
          <line x1="78" y1="42" x2="42" y2="78" />
        </g>
        <g stroke={`url(#${uid}Gold)`} strokeWidth="12">
          <line x1="42" y1="42" x2="78" y2="78" />
          <line x1="78" y1="42" x2="42" y2="78" />
        </g>
      </g>
    </svg>
  );
}

// Wordmark casing is locked: X c e l i o
export function Wordmark({ className = "", mono = null }) {
  const color = mono === "white" ? "text-white" : mono === "black" ? "text-black" : "";
  return (
    <span className={`font-sans font-light tracking-[0.06em] ${color} ${className}`} aria-label="Xcelio">
      <span>X</span><span className="lowercase">celio</span>
    </span>
  );
}

// variant: horizontal | stacked | icon | wordmark
export default function Logo({ variant = "horizontal", size = 34, mono = null, className = "", wordClass = "text-2xl" }) {
  if (variant === "icon") return <LogoMark size={size} mono={mono} className={className} />;
  if (variant === "wordmark") return <Wordmark className={`${wordClass} ${className}`} mono={mono} />;
  if (variant === "stacked")
    return (
      <div className={`flex flex-col items-center gap-2 ${className}`}>
        <LogoMark size={size} mono={mono} />
        <Wordmark className={wordClass} mono={mono} />
      </div>
    );
  return (
    <span className={`inline-flex items-center gap-2.5 ${className}`}>
      <LogoMark size={size} mono={mono} />
      <Wordmark className={wordClass} mono={mono} />
    </span>
  );
}
